import {Select} from '@components/form-fields/Inputs/Select';
import {useForm} from 'react-hook-form';
import {Checkbox} from '@components/form-fields/Inputs/Checkbox';
import {useEffect} from 'react';
import {
  AddToCartButtonMultipleVariants
} from '@components/product/global/AddToCartMultipleVariants';
import {CART_BUTTON_PRIMARY_CLASSES} from '@components/cart/CartButtonClasses';
import {
  SingleProductFocusPricing
} from '@components/product/ProductPageTemplates/Components/SingleProductFocusWithAssets/SingleProductFocusPricing';

export const SoundDeadeningCalculator = ({normalizedProducts}) => {
  const {
    register,
    formState: {errors},
    watch,
    setValue,
    setError
  } = useForm({
    defaultValues: {
      vehicleType: '-',
      coverageArea: []
    }
  });

  const childVariants = normalizedProducts?.childVariants;

  const vehicleOptions = [
    {
      label: 'Select Vehicle Type',
      value: '-'
    },
    ...Object.keys(vehicleTypes).map((vehicleType) => {
      return {
        label: vehicleTypes[vehicleType].label,
        value: vehicleType
      };
    })
  ];

  const vehicleType = watch('vehicleType');
  const coverageArea = watch('coverageArea');

  useEffect(() => {
    if (coverageArea?.length > 0 && (!vehicleType || vehicleType === '-')) {
      console.log('setting error on vehicleType');
      setError('vehicleType', {type: 'custom', message: 'Please choose a vehicle'});
    } else {
      setError('vehicleType', null);
    }
  }, [coverageArea]);

  const totalSquareFeet = calculateSquareFootageByVehicleType(vehicleType, coverageArea);

  const vehicleTypeErrorMessage = errors?.vehicleType?.message;

  const SK2MMSkuData = createSkuDataArray(childVariants);
  // console.log(SK2MMSkuData);

  const skusForSquareFeet = findSkusForSquareFeet(totalSquareFeet, SK2MMSkuData);
  const {bestCombinationVariants, bestCombinationCost, bestCombinationSqFt} = skusForSquareFeet;
  // console.log({bestCombinationVariants, bestCombinationCost, bestCombinationSqFt});

  const combinedVariantsPricingProduct = sumVariantsPricing(bestCombinationVariants);

  return (
    <div className={'w-full mt-4 mb-8 lg:mt-0 lg:w-full'}>
      <div className={'mt-4 lg:mt-0 grid grid-cols-3 gap-4'}>
        <div className={'col-span-2'}>
          <VehicleTypeSelect
            register={register}
            vehicleOptions={vehicleOptions}
            vehicleType={vehicleType}
            vehicleTypeErrorMessage={vehicleTypeErrorMessage}
          />
        </div>
      </div>

      {vehicleType ? (
        <div className={'w-full lg:grid lg:grid-cols-3 mt-4 gap-4'}>
          <div className={'lg:col-span-2 mt-4 lg:mt-0'}>
            <VehicleTypeCoverageAreas
              register={register}
              totalSquareFeet={totalSquareFeet}
              bestCombinationSqFt={bestCombinationSqFt}
            />
          </div>

          {bestCombinationSqFt > 0 ? (
            <>
              <div className={'lg:col-span-3 mt-4 lg:mt-0'}>
                <BestCombinationVariants
                  bestCombinationSqFt={bestCombinationSqFt}
                  bestCombinationVariants={bestCombinationVariants}
                />
              </div>

              <div className={'lg:col-span-2 mt-2 lg:mt-4'}>
                <BestCombinationPricing
                  bestCombinationSqFt={bestCombinationSqFt}
                  bestCombinationVariants={bestCombinationVariants}
                  combinedVariantsPricingProduct={combinedVariantsPricingProduct}
                />
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const VehicleTypeSelect = ({register, vehicleOptions, vehicleTypeErrorMessage, vehicleType}) => {
  const hasVehicleTypeErrorMessage = vehicleTypeErrorMessage && !vehicleType ? true : false;
  return (
    <div>
      <label
        htmlFor='vehicleType'
        className={'mb-2 text-sm lg:text-md font-bold uppercase leading-none'}>
        Vehicle Type
      </label>
      <Select
        id={'vehicleType'}
        name='vehicleType'
        register={register}
        options={vehicleOptions}
        validations={{
          required: 'Please select a vehicle type'
        }}
        className={'relative peer w-full'}
        inputClassName={`rounded border border-neutral-600 bg-white dark:bg-white active:border ${
          hasVehicleTypeErrorMessage ? ' border-skar-red' : null
        }`}
      />
      {hasVehicleTypeErrorMessage ? (
        <div className={'text-skar-red px-2'}>{vehicleTypeErrorMessage}</div>
      ) : null}
    </div>
  );
};

const VehicleTypeCoverageAreas = ({register, totalSquareFeet, bestCombinationSqFt}) => {
  return (
    <div>
      <label
        htmlFor='coverageArea'
        className={
          'flex gap-2 items-center mb-2 text-sm lg:text-md font-bold uppercase leading-none'
        }>
        <div className={''}>Coverage Areas</div>
        {bestCombinationSqFt > 0 && (
          <div className={'text-xs'}>{`(Est. ${totalSquareFeet} SF)`}</div>
        )}
      </label>
      <div className={'flex lg:grid lg:grid-cols-4 gap-x-4 gap-y-2 lg:gap-2 lg:text-lg'}>
        {Object.keys(coverageAreaTypes)?.map((coverageAreaType) => {
          return (
            <div key={coverageAreaType}>
              <Checkbox
                register={register}
                id={'coverageArea'}
                name={'coverageArea'}
                value={coverageAreaType}
                className={'w-5 h-5'}
                label={coverageAreaTypes[coverageAreaType].label}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BestCombinationVariants = ({bestCombinationSqFt, bestCombinationVariants}) => {
  return (
    <>
      {bestCombinationSqFt > 0 ? (
        <>
          <div
            className={`flex gap-2 items-center mb-2 text-sm lg:text-md font-bold uppercase leading-none`}>
            <div className={'text-md font-bold'}>{`Best Kits Combo`}</div>
            <div className={'text-xs font-bold'}>{`(${bestCombinationSqFt} SF)`}</div>
          </div>

          <div className={'flex flex-wrap items-start gap-2 lg:grid-cols-1'}>
            {bestCombinationVariants.map((childVariant) => {
              const title = childVariant?.title;
              const quantity = childVariant?.quantity;
              const sqft = childVariant?.sqft;

              return (
                <div
                  key={title}
                  className='flex gap-2 px-2 py-1 lg:px-4 lg:py-2 rounded border border-neutral-400 bg-neutral-200 text-sm'>
                  {`${quantity} x ${title} (${sqft} SF)`}
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </>
  );
};

const BestCombinationPricing = ({
  bestCombinationSqFt,
  bestCombinationVariants,
  combinedVariantsPricingProduct
}) => {
  return (
    <>
      {bestCombinationSqFt > 0 ? (
        <div className={'space-y-4'}>
          <SingleProductFocusPricing product={combinedVariantsPricingProduct} />
          <AddToCartButtonMultipleVariants
            className={CART_BUTTON_PRIMARY_CLASSES}
            selectedVariants={bestCombinationVariants}
            addToCartButtonSource={'SK2MM-Calculator'}>
            Add Items To Cart
          </AddToCartButtonMultipleVariants>
        </div>
      ) : null}
    </>
  );
};

export const createSkuDataArray = (data) => {
  return data.map((item) => {
    const sheets = parseInt(item.quickFactsTable['Number of Sheets'].split(' ')[0], 10);
    const sqft = parseInt(item.quickFactsTable['Total Square Footage'].split(' ')[0], 10);

    return {
      sheets: sheets,
      sqft: sqft,
      price: item?.price,
      sku: item?.sku,
      id: item?.product_id,
      title: item?.variantOptionOneValue,
      isSaleEnabled: item?.isSaleEnabled,
      isInStock: item?.isInStock,
      priceMap: item?.priceMap,
      priceMsrp: item?.priceMsrp,
      priceDealer: item?.priceDealer
    };
  });
};

export const calculateSquareFootageByVehicleType = (vehicleType, selectedCoverageAreas) => {
  let totalSquareFootage = 0;

  if (vehicleType) {
    const selectedVehicleData = vehicleTypes[vehicleType];
    const estimatedSquareFootage = selectedVehicleData?.estimatedSquareFootage;

    if (estimatedSquareFootage) {
      Object?.keys(estimatedSquareFootage)?.forEach((coverageAreaType) => {
        if (selectedCoverageAreas?.includes(coverageAreaType)) {
          totalSquareFootage += estimatedSquareFootage[coverageAreaType];
        }
      });
    }
  }

  return totalSquareFootage;
};

export const sumVariantsPricing = (products) => {
  return products?.reduce((acc, product) => {
    return {
      ...acc,
      price: (acc?.price || 0) + product?.price * product?.quantity,
      priceMap: (acc?.priceMap || 0) + product?.priceMap * product?.quantity,
      priceMsrp: (acc?.priceMsrp || 0) + product?.priceMsrp * product?.quantity,
      priceDealer: (acc?.priceDealer || 0) + product?.priceDealer * product?.quantity
    };
  }, {});
};

/**
 * This function finds the most cost-effective combination of SKUs to cover the desired total square footage.
 * When factoring in the best combination, the total cost is rounded up to the nearest 10 dollars, to make it easier to select the minimal number of SKUs.
 *
 * @param {number} totalSqFtRequired - The total square footage that needs to be covered.
 * @returns {Object} - An object containing:
 *   - bestCombinationSkus: The SKUs and their quantities that make up the best combination.
 *   - bestCombinationCost: The actual total cost of the best combination.
 *   - bestCombinationSqFt: The total square footage covered by the best combination.
 */
function findSkusForSquareFeet(totalSqFtRequired, skuData) {
  const skus = skuData?.sort((a, b) => a?.price - b?.price);
  const memo = {};

  function findCombination(totalSqFt, currentCost, currentCombination) {
    if (totalSqFt <= 0) {
      return {cost: currentCost, combination: currentCombination, totalSqFt: -totalSqFt};
    }

    const memoKey = `${totalSqFt}-${currentCost}`;
    if (memo[memoKey]) {
      return memo[memoKey];
    }

    let bestCombination = null;
    for (const sku of skus) {
      const newTotalSqFt = totalSqFt - sku?.sqft;
      const newCost = currentCost + sku?.price;
      const newCombination = {
        ...currentCombination,
        [sku?.sku]: (currentCombination[sku?.sku] || 0) + 1
      };
      const combination = findCombination(newTotalSqFt, newCost, newCombination);

      if (!bestCombination) {
        bestCombination = combination;
      } else {
        const bestRoundedCost = Math?.ceil(bestCombination?.cost / 10) * 10;
        const roundedCost = Math?.ceil(combination?.cost / 10) * 10;
        const isCostLower = roundedCost < bestRoundedCost;
        const isSameCostButFewerSkusAndQuantities =
          roundedCost === bestRoundedCost &&
          (Object?.keys(combination?.combination).length <
            Object?.keys(bestCombination?.combination).length ||
            (Object?.keys(combination?.combination).length ===
              Object?.keys(bestCombination?.combination).length &&
              Object?.values(combination?.combination)?.reduce((acc, val) => acc + val, 0) <
                Object?.values(bestCombination?.combination)?.reduce((acc, val) => acc + val, 0)));

        if (isCostLower || isSameCostButFewerSkusAndQuantities) {
          bestCombination = combination;
        }
      }
    }

    memo[memoKey] = bestCombination;
    return bestCombination;
  }

  const bestCombination = findCombination(totalSqFtRequired, 0, {});

  const bestCombinationVariants = Object.entries(bestCombination?.combination).map(
    ([sku, quantity]) => {
      const skuDataItem = skuData?.find((item) => item?.sku === sku);
      return {...skuDataItem, quantity};
    }
  );

  return {
    bestCombinationVariants,
    bestCombinationCost: Math.round(Math?.ceil(bestCombination?.cost / 10) * 10),
    bestCombinationSqFt: totalSqFtRequired + bestCombination.totalSqFt
  };
}

const squareFootagePerSheet = 4; // 3.9375

const coverageAreaTypes = {
  floor: {
    label: 'Floor'
  },
  doors: {
    label: 'Doors'
  },
  trunk: {
    label: 'Trunk'
  },
  roof: {
    label: 'Roof'
  }
};

const vehicleTypes = {
  twoDoor: {
    label: '2-Door Car',
    estimatedSquareFootage: {
      floor: 17.5,
      doors: 25,
      trunk: 12.5,
      roof: 25
    }
  },
  fourDoorSedan: {
    label: '4-Door Car',
    estimatedSquareFootage: {
      floor: 27.5,
      doors: 40,
      trunk: 22.5,
      roof: 35
    }
  },
  suv: {
    label: 'SUV',
    estimatedSquareFootage: {
      floor: 35,
      doors: 45,
      trunk: 25,
      roof: 45
    }
  },
  crossover: {
    label: 'Crossover',
    estimatedSquareFootage: {
      floor: 35,
      doors: 45,
      trunk: 25,
      roof: 45
    }
  },
  twoDoorTruck: {
    label: '2-Door Truck',
    estimatedSquareFootage: {
      floor: 25,
      doors: 35,
      trunk: 0,
      roof: 25
    }
  },
  fourDoorTruck: {
    label: '4-Door Truck',
    estimatedSquareFootage: {
      floor: 25,
      doors: 35,
      trunk: 0,
      roof: 25
    }
  }
};
